import RestroCard, { PromotedRestroCard } from "./RestroCard";
import { useEffect, useState, useContext, useMemo } from "react";
import { Link } from "react-router";
import Shimmer from "./Shimmer";
import OnlineStatus from "../utils/useOnlineStatus";
import { API_URL } from "../utils/constants";
import UserContext from "../utils/UserContext";

const Body = () => {
    const { defaultUser, setUserName } = useContext(UserContext);
    const [filteredRestaurants, setFilteredRestaurants] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [allRestaurants, setAllRestaurants] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const onlineStatus = OnlineStatus();
    const PromotedCard = PromotedRestroCard(RestroCard);
    if (!onlineStatus) {
        return <h1>You are offline — please check your connection</h1>;
    }
    const fetchData = async () => {
        const res = await fetch(API_URL);
        const data = await res.json();
        const restaurants = data?.restaurants || [];
        setFilteredRestaurants(restaurants);
        setAllRestaurants(restaurants);
        setIsLoading(false);
    };
    useEffect(() => {
        fetchData();
    }, []);
    const debounce = (func, delay) => {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), delay);
        };
    };

    const debouncedSearch = useMemo(
        () =>
            debounce(async (text) => {
                try {
                    const searchData = awai(`${API_URL}/search?query=${text}`);
                    const data = await searchData.json();
                    setFilteredRestaurants(data.restaurants || []);
                } catch (err) {
                    console.error("Search failed:", err);
                }
            }, 500),
        [],
    );

    useEffect(() => {
        if (searchText === "") {
            setFilteredRestaurants(allRestaurants);
        } else {
            debouncedSearch(searchText);
        }
    }, [searchText]);

    if (isLoading) return <Shimmer />;

    return (
        <div className="px-8 pb-12">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-3 py-6 border-b border-gray-100 mb-8">
                {/* Search */}
                <div className="flex items-center border border-gray-300 rounded-full overflow-hidden bg-white shadow-sm flex-1 min-w-[200px] max-w-xs">
                    <input
                        type="text"
                        placeholder="Search restaurants..."
                        className="flex-1 px-4 py-2 text-sm outline-none bg-transparent"
                        data-testid="searchInput"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                    {searchText && (
                        <button className="pr-4 text-gray-400 hover:text-gray-600" onClick={() => setSearchText("")}>
                            ✕
                        </button>
                    )}
                </div>

                {/* Filters */}
                <button
                    className="px-5 py-2 text-sm font-semibold border border-gray-300 rounded-full bg-white shadow-sm hover:bg-orange-50 hover:border-orange-400 hover:text-orange-600 transition-all whitespace-nowrap"
                    onClick={() => setFilteredRestaurants(allRestaurants.filter((r) => r.avgRating > 4.2))}
                >
                    ⭐ Top Rated
                </button>
                <button
                    className="px-5 py-2 text-sm font-semibold border border-gray-300 rounded-full bg-white shadow-sm hover:bg-gray-50 transition-all whitespace-nowrap"
                    onClick={() => setFilteredRestaurants(allRestaurants)}
                >
                    All
                </button>

                {/* User name — pushed to right */}
                <div className="flex items-center gap-2 ml-auto">
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                        User
                    </label>
                    <input
                        type="text"
                        placeholder="Your name"
                        value={defaultUser || ""}
                        onChange={(e) => setUserName(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-full outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 w-36"
                    />
                </div>
            </div>

            {/* Restaurant Grid or Empty State */}
            {filteredRestaurants.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                    <span className="text-5xl mb-4">🔍</span>
                    <h2 className="text-xl font-bold text-gray-700 mb-2">No restaurants found</h2>
                    <p className="text-gray-400 text-sm mb-6">
                        We couldn't find any results for "
                        <span className="font-semibold text-gray-600">{searchText}</span>"
                    </p>
                    <button
                        className="px-5 py-2 text-sm font-semibold bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors"
                        onClick={() => setSearchText("")}
                    >
                        Clear search
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                    {filteredRestaurants.map((res) => (
                        <Link key={res.id} to={"restaurant/" + res.id}>
                            {res.avgRating > 4.0 ? <PromotedCard {...res} /> : <RestroCard {...res} />}
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
};
export default Body;
